
import React, { useMemo } from 'react';
import { CurriculumNode, SubjectType } from '../types';
import { Lock, Check, Star } from 'lucide-react';

interface GalaxyMapProps {
  nodes: CurriculumNode[];
  unlockedSkills: string[];
  theme: { bg: string, text: string, gradient: string, shadow: string };
  onNodeSelect: (node: CurriculumNode) => void;
  subjectTitle: string;
}

// Helper to calculate orbital positions
const calculateLayout = (nodes: CurriculumNode[], width: number, height: number) => {
  const centerX = width / 2;
  const centerY = height / 2;
  const layout: Record<string, { x: number, y: number, node: CurriculumNode }> = {};

  // Simple Spiral Algorithm for "Constellation" look
  // Start from center, spiral out
  const a = 60; // spacing
  const b = 60; 
  
  nodes.forEach((node, index) => {
    // Determine level-based ring or just sequence
    // Using a golden angle spiral for natural distribution
    const angle = index * 2.4; // rough golden angle in radians adjustment
    const radius = 50 + (30 * index); // slowly moving out
    
    // Zig-zag spiral for more horizontal coverage on desktop
    const x = centerX + Math.cos(angle) * (radius * 1.5); 
    const y = centerY + Math.sin(angle) * radius;

    layout[node.id] = { x, y, node };
  });

  return layout;
};

export const GalaxyMap: React.FC<GalaxyMapProps> = ({ nodes, unlockedSkills, theme, onNodeSelect, subjectTitle }) => {
  const width = 800;
  const height = 600;

  const layout = useMemo(() => calculateLayout(nodes, width, height), [nodes]);
  const nodeIds = nodes.map(n => n.id);

  return (
    <div className="w-full h-[600px] bg-slate-900/40 rounded-[2.5rem] border border-white/10 relative overflow-hidden shadow-inner group select-none">
      
      {/* Background Ambience */}
      <div className={`absolute inset-0 opacity-20 bg-gradient-to-br ${theme.gradient} to-transparent pointer-events-none`}></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-white/5 rounded-full blur-[100px] pointer-events-none"></div>

      {/* Title Watermark */}
      <div className="absolute bottom-6 right-8 text-right pointer-events-none opacity-50">
        <h3 className="text-4xl font-black text-white/10 font-orbitron uppercase">{subjectTitle}</h3>
        <p className="text-xs font-bold text-white/20 tracking-[0.5em]">GALAXY VIEW</p>
      </div>

      {/* Interactive SVG Layer */}
      <svg className="w-full h-full absolute inset-0 pointer-events-none z-0" viewBox={`0 0 ${width} ${height}`}>
        <defs>
           <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
             <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
             <feMerge>
               <feMergeNode in="coloredBlur"/>
               <feMergeNode in="SourceGraphic"/>
             </feMerge>
           </filter>
        </defs>
        
        {/* Connection Lines (Constellations) */}
        {nodes.map(node => {
           if (!node.prev || !layout[node.prev]) return null;
           const start = layout[node.prev];
           const end = layout[node.id];
           const isUnlocked = unlockedSkills.includes(node.prev);
           
           return (
             <g key={`link-${node.id}`}>
                {/* Background Line */}
                <line 
                  x1={start.x} y1={start.y} x2={end.x} y2={end.y} 
                  stroke="rgba(255,255,255,0.1)" 
                  strokeWidth="4"
                  strokeLinecap="round"
                />
                {/* Active Flow Line */}
                {isUnlocked && (
                    <line 
                    x1={start.x} y1={start.y} x2={end.x} y2={end.y} 
                    stroke="white" 
                    className="text-white opacity-40"
                    strokeWidth="1"
                    strokeDasharray="4 4"
                  >
                    <animate attributeName="stroke-dashoffset" from="100" to="0" dur="2s" repeatCount="indefinite" />
                  </line>
                )}
             </g>
           );
        })}
      </svg>

      {/* Nodes Layer (HTML for easier interaction/tooltips) */}
      <div className="relative w-full h-full z-10">
        {nodes.map((node, i) => {
           const pos = layout[node.id];
           const isCompleted = unlockedSkills.includes(node.id);
           const isUnlocked = node.prev === null || unlockedSkills.includes(node.prev);
           const isLocked = !isUnlocked;
           
           return (
             <div
                key={node.id}
                onClick={() => isUnlocked && onNodeSelect(node)}
                className={`absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-500 cursor-pointer group/node`}
                style={{ left: pos.x, top: pos.y }}
             >
                {/* Pulse Effect for Unlocked/Next Node */}
                {isUnlocked && !isCompleted && (
                    <div className={`absolute inset-0 rounded-full animate-ping opacity-75 ${theme.bg}`}></div>
                )}

                {/* The Planet Node */}
                <div className={`
                    w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center border-2 shadow-2xl relative z-10 transition-transform hover:scale-110
                    ${isCompleted 
                      ? 'bg-emerald-500 border-emerald-300 text-white shadow-[0_0_20px_rgba(16,185,129,0.6)]' 
                      : isUnlocked 
                        ? `${theme.bg} border-white text-white shadow-[0_0_20px_rgba(255,255,255,0.4)]`
                        : 'bg-slate-800 border-slate-700 text-slate-500 grayscale'}
                `}>
                    {isCompleted ? <Check size={24} strokeWidth={4} /> : isLocked ? <Lock size={20} /> : <span className="text-lg font-black">{i+1}</span>}
                </div>

                {/* Label Tooltip */}
                <div className={`
                    absolute top-full mt-3 left-1/2 -translate-x-1/2 whitespace-nowrap px-4 py-2 rounded-xl text-center backdrop-blur-md border transition-all duration-300
                    ${isLocked 
                        ? 'bg-black/40 border-white/5 text-slate-500 scale-90 opacity-0 group-hover/node:opacity-100 group-hover/node:scale-100' 
                        : 'bg-slate-900/80 border-white/20 text-white shadow-xl scale-100'}
                `}>
                    <div className="text-xs font-bold">{node.title}</div>
                    {isUnlocked && !isCompleted && (
                        <div className="text-[9px] font-black uppercase tracking-wider text-indigo-300 animate-pulse mt-0.5">Start Mission</div>
                    )}
                </div>
             </div>
           );
        })}
        
        {/* Central Star (Subject Core) */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1 pointer-events-none">
             <div className={`w-[600px] h-[600px] -translate-x-1/2 -translate-y-1/2 radial-gradient opacity-10 rounded-full ${theme.bg}`}></div>
        </div>
      </div>

    </div>
  );
};
